/**
 * Trophy Room Redux Slice
 * Story 3.6.d: Owner Trophy Room Management Frontend
 *
 * Manages state for authenticated owner's Trophy Room.
 * Supports CRUD operations with optimistic updates and rollback.
 * Uses gateway injection pattern for testability.
 *
 * Operations:
 * - Fetch user items (GET /api/v1/owner/trophy-room/items)
 * - Toggle visibility (PUT /api/v1/owner/trophy-room/items/:itemId/visibility)
 * - Unpair item (POST /api/v1/owner/trophy-room/items/:itemId/unpair?reportType={type})
 *   - Merged operation: Optionally report item status (lost/stolen/sold/unused) before unpair
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState, StoreExtraDependencies } from '../index';
import type { TrophyRoomItem } from '@/domain/ports/ITrophyRoomGateway';

/**
 * Trophy Room state structure
 */
export interface TrophyRoomState {
  // Data
  items: TrophyRoomItem[];
  totalCount: number;

  // Loading & Error states
  loading: boolean;
  error: string | null;

  // Operation-specific loading states
  updatingVisibility: string | null; // itemId being updated
  unpairing: string | null; // itemId being unpaired (with optional report)
}

const initialState: TrophyRoomState = {
  items: [],
  totalCount: 0,
  loading: false,
  error: null,
  updatingVisibility: null,
  unpairing: null,
};

/**
 * Async Thunk: Fetch all Trophy Room items for authenticated owner
 */
export const getUserItemsAsync = createAsyncThunk<
  { items: TrophyRoomItem[]; totalCount: number },
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('trophyRoom/getUserItems', async (_, { extra, rejectWithValue }) => {
  try {
    const { trophyRoomGateway } = extra as StoreExtraDependencies & {
      trophyRoomGateway: import('@/domain/ports/ITrophyRoomGateway').ITrophyRoomGateway;
    };

    return await trophyRoomGateway.getUserItems();
  } catch (error) {
    console.error('[trophyRoomSlice] getUserItems failed:', error);

    if (error instanceof Error) {
      if (error.message.includes('Unauthorized')) {
        return rejectWithValue('Please log in to view your Trophy Room.');
      }
      if (error.message.includes('NetworkError')) {
        return rejectWithValue('Network error. Please check your connection and try again.');
      }
      return rejectWithValue(error.message);
    }

    return rejectWithValue('Failed to load Trophy Room items');
  }
});

/**
 * Async Thunk: Update item visibility (public/private)
 * Uses optimistic update with rollback on error
 */
export const updateItemVisibilityAsync = createAsyncThunk<
  TrophyRoomItem,
  { itemId: string; visibility: 'public' | 'private' },
  { extra: StoreExtraDependencies; state: RootState; rejectValue: string }
>(
  'trophyRoom/updateVisibility',
  async ({ itemId, visibility }, { extra, rejectWithValue }) => {
    try {
      const { trophyRoomGateway } = extra as StoreExtraDependencies & {
        trophyRoomGateway: import('@/domain/ports/ITrophyRoomGateway').ITrophyRoomGateway;
      };

      return await trophyRoomGateway.updateItemVisibility(itemId, visibility);
    } catch (error) {
      console.error('[trophyRoomSlice] updateVisibility failed:', error);

      if (error instanceof Error) {
        if (error.message.includes('permission')) {
          return rejectWithValue('You do not have permission to modify this item.');
        }
        if (error.message.includes('not found')) {
          return rejectWithValue('Item not found. It may have been deleted.');
        }
        if (error.message.includes('NetworkError')) {
          return rejectWithValue('Network error. Please try again.');
        }
        return rejectWithValue(error.message);
      }

      return rejectWithValue('Failed to update visibility');
    }
  }
);

/**
 * Async Thunk: Unpair item (remove from Trophy Room)
 * Uses optimistic update with rollback on error (re-fetch)
 *
 * Now supports optional reportType parameter for merged unpair/report workflow:
 * - If reportType provided: Reports item status before unpairing
 * - If reportType undefined: Simple unpair without status change
 */
export const unpairItemAsync = createAsyncThunk<
  string, // Returns itemId
  { itemId: string; reportType?: 'lost' | 'stolen' | 'sold' | 'unused' }, // Updated signature
  { extra: StoreExtraDependencies; state: RootState; rejectValue: string }
>('trophyRoom/unpairItem', async ({ itemId, reportType }, { extra, rejectWithValue }) => {
  try {
    const { trophyRoomGateway } = extra as StoreExtraDependencies & {
      trophyRoomGateway: import('@/domain/ports/ITrophyRoomGateway').ITrophyRoomGateway;
    };

    await trophyRoomGateway.unpairItem(itemId, reportType);
    return itemId;
  } catch (error) {
    console.error('[trophyRoomSlice] unpairItem failed:', error);

    if (error instanceof Error) {
      if (error.message.includes('permission')) {
        return rejectWithValue('You do not have permission to unpair this item.');
      }
      if (error.message.includes('NetworkError')) {
        return rejectWithValue('Network error. Please try again.');
      }
      return rejectWithValue(error.message);
    }

    return rejectWithValue('Failed to unpair item');
  }
});


/**
 * Trophy Room Slice
 */
const trophyRoomSlice = createSlice({
  name: 'trophyRoom',
  initialState,
  reducers: {
    /**
     * Clear Trophy Room data (e.g., when logging out)
     */
    clearTrophyRoom() {
      return initialState;
    },

    /**
     * Clear error message
     */
    clearError(state) {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // ========== Get User Items ==========
      .addCase(getUserItemsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserItemsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(getUserItemsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load Trophy Room items';
      })

      // ========== Update Visibility (Optimistic) ==========
      .addCase(updateItemVisibilityAsync.pending, (state, action) => {
        state.updatingVisibility = action.meta.arg.itemId;
        state.error = null;

        // Optimistic update
        const item = state.items.find((i) => i.url === action.meta.arg.itemId);
        if (item) {
          item.visibility = action.meta.arg.visibility;
        }
      })
      .addCase(updateItemVisibilityAsync.fulfilled, (state, action) => {
        state.updatingVisibility = null;

        // Update with server response (ensure consistency)
        const index = state.items.findIndex((i) => i.url === action.payload.url);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateItemVisibilityAsync.rejected, (state, action) => {
        state.updatingVisibility = null;
        state.error = action.payload || 'Failed to update visibility';

        // Rollback: Re-fetch items
        // Note: ViewModel will trigger getUserItemsAsync on error
      })

      // ========== Unpair Item (Optimistic) ==========
      .addCase(unpairItemAsync.pending, (state, action) => {
        state.unpairing = action.meta.arg.itemId;
        state.error = null;

        // Optimistic update: Remove item
        state.items = state.items.filter((i) => i.url !== action.meta.arg.itemId);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(unpairItemAsync.fulfilled, (state) => {
        state.unpairing = null;
        // Item already removed optimistically
      })
      .addCase(unpairItemAsync.rejected, (state, action) => {
        state.unpairing = null;
        state.error = action.payload || 'Failed to unpair item';

        // Rollback: Re-fetch items
        // Note: ViewModel will trigger getUserItemsAsync on error
      });
  },
});

// Actions
export const { clearTrophyRoom, clearError } = trophyRoomSlice.actions;

// Selectors
export const selectTrophyRoom = (state: RootState) => state.trophyRoom;

export const selectTrophyRoomLoading = (state: RootState) =>
  state.trophyRoom?.loading ?? false;

export const selectTrophyRoomError = (state: RootState) =>
  state.trophyRoom?.error ?? null;

export const selectTrophyRoomItems = (state: RootState) =>
  state.trophyRoom?.items ?? [];

export const selectTrophyRoomTotalCount = (state: RootState) =>
  state.trophyRoom?.totalCount ?? 0;

// Operation-specific loading selectors
export const selectUpdatingVisibility = (state: RootState) =>
  state.trophyRoom?.updatingVisibility ?? null;

export const selectUnpairing = (state: RootState) =>
  state.trophyRoom?.unpairing ?? null;

// Reducer
export default trophyRoomSlice.reducer;
